// 권위:
//   - docs/ARCH_DASHBOARD.md §4.5 — 화면 5 LiveMonitor 중앙 패널.
//   - data/reports/UNIMPLEMENTED_INVENTORY.md §4 — 책임 분리:
//       /live LiveMonitor       : 시스템 총괄 — 전체 이벤트 / **간략 PnL 요약** / Circuit Breaker
//       /trading/live           : 트레이딩 깊이 — 시그널 flow / Slot trigger
//       /positions              : 포지션 상세 (화면 2 흡수)
//
// 직전 사이클에 화면 2 LivePositions 가 /positions (TradingPositions) 로 흡수됨에 따라
// 본 중앙 패널의 placeholder 문구는 *죽은 코드* 가 되었다.
// 이를 정정하여 **간략 PnL summary** 로 교체한다.
//   - 보유 종목 수
//   - 총 평가금액
//   - 평가손익률 (전체 합산)
//   - "/positions" 상세 링크
//
// 데이터 source: GET /api/v1/trading/positions 응답을 frontend 에서 sum/aggregate.
// (별도 PnL summary endpoint 신설 회피 — 운영 API 한 곳에 집중.)

import { Link } from 'react-router-dom';
import { useTradingPositionsQuery } from '../Trading/tradingApi';
import { pnlPct, type PositionRow } from '../Trading/tradingMock';
import '../Trading/trading.css';

interface Props {
  /** 테스트 주입용 — 미지정 시 API 호출. */
  rows?: PositionRow[];
}

export function LiveMonitorPositions(props: Props): JSX.Element {
  const injected = props.rows;
  const enabled = injected === undefined;
  const { data, isLoading, isError } = useTradingPositionsQuery({}, { enabled });
  const rows = injected ?? data?.rows ?? [];
  const summary = aggregate(rows);

  return (
    <section className="live-panel" aria-label="포지션 PnL 요약">
      <header className="live-panel__header">
        <h2 className="live-panel__title">PnL 요약</h2>
        <Link to="/positions" className="live-pnl-summary__link">
          상세 →
        </Link>
      </header>

      <div className="live-panel__body">
        {!injected && isLoading && (
          <div className="trading-table__empty">불러오는 중…</div>
        )}
        {!injected && isError && (
          <div className="trading-page__notice" role="alert">
            <strong>오류 —</strong>
            포지션 조회 실패. 잠시 후 다시 시도하세요.
          </div>
        )}
        {(injected !== undefined || !isLoading) && (
          <div className="live-pnl-summary" data-testid="live-pnl-summary">
            <div className="live-pnl-summary__grid">
              <div className="live-pnl-summary__cell">
                <span className="live-pnl-summary__label">보유 종목</span>
                <span className="live-pnl-summary__value">{summary.count}</span>
              </div>
              <div className="live-pnl-summary__cell">
                <span className="live-pnl-summary__label">총 평가금액</span>
                <span className="live-pnl-summary__value">
                  {summary.marketValue == null ? '–' : formatKrw(summary.marketValue)}
                </span>
              </div>
              <div className="live-pnl-summary__cell">
                <span className="live-pnl-summary__label">평가손익률</span>
                <span
                  className={`live-pnl-summary__value ${pnlClass(summary.totalPnlPct)}`}
                >
                  {summary.totalPnlPct == null ? '–' : formatPct(summary.totalPnlPct)}
                </span>
              </div>
              <div className="live-pnl-summary__cell">
                <span className="live-pnl-summary__label">평가손익</span>
                <span className={`live-pnl-summary__value ${pnlClass(summary.totalPnl)}`}>
                  {summary.totalPnl == null ? '–' : formatKrw(summary.totalPnl)}
                </span>
              </div>
            </div>
            {summary.priceGap && (
              <div className="trading-page__notice" role="note">
                <strong>주의 —</strong>
                일부 종목 시세 부재로 총합이 신뢰 불가합니다.
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

interface Aggregate {
  count: number;
  marketValue: number | null;
  totalPnl: number | null;
  totalPnlPct: number | null;
  priceGap: boolean;
}

function aggregate(rows: PositionRow[]): Aggregate {
  if (rows.length === 0) {
    return { count: 0, marketValue: 0, totalPnl: 0, totalPnlPct: 0, priceGap: false };
  }
  let marketValue = 0;
  let cost = 0;
  let priceGap = false;
  for (const r of rows) {
    if (!Number.isFinite(r.currentPrice)) {
      priceGap = true;
    } else {
      marketValue += r.currentPrice * r.qty;
    }
    cost += r.avgEntry * r.qty;
    // pnlPct 호출은 행별 NaN 가드 (집계엔 사용하지 않지만 import 정합 유지).
    void pnlPct(r);
  }
  if (priceGap) {
    return {
      count: rows.length,
      marketValue: null,
      totalPnl: null,
      totalPnlPct: null,
      priceGap: true,
    };
  }
  const totalPnl = marketValue - cost;
  const totalPnlPct = cost > 0 ? (totalPnl / cost) * 100 : 0;
  return { count: rows.length, marketValue, totalPnl, totalPnlPct, priceGap: false };
}

function pnlClass(value: number | null): string {
  if (value == null) return 'trading-num--zero';
  if (value > 0.01) return 'trading-num--up';
  if (value < -0.01) return 'trading-num--down';
  return 'trading-num--zero';
}

function formatKrw(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return new Intl.NumberFormat('ko-KR').format(Math.round(value));
}

function formatPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}
